// OVO-113 — Schedule editor modal (admin only).
//
// Used by PartnerSites' expanded-row UI to create / edit a recurring
// order rule. OVO-147 added the `defaultProducts` picker: rows of
// (partner item type · quantity · optional weight) copied verbatim onto
// every materialized order as OrderProducts. Leaving weight blank on an
// item type that requires it means the DRIVER captures it at pickup
// (the existing pickup-weight-enrichment flow).
//
// `deliverySiteId` is required and pre-filled from the parent context
// (the operator clicked "Add schedule" on a specific Site row).
//
// #170 — the box (collection point) the task collects, picked among the
// pickup site's boxes: a site can hold several, and only the task knows which
// one it is. #169 — `showPosition` (FIXED_TOURS partners) adds the stop order
// inside the tournée, read by the route generation.

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DatePicker, Form, Input, InputNumber, Modal, Select, Switch, Tag } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { createSchedule, updateSchedule } from '../modules/order-schedules/api';
import { listSitesForPartner } from '../modules/sites/api';
import { getPartnerItemTypes } from '../modules/partners/api';
import { listCollectionPointsForPartner } from '../modules/collection-points/api';
import { buildScheduleDto } from '../utils/scheduleDto';
import { boxLabel } from '../utils/scheduleBox';
import LinkedTicketsPanel from './LinkedTicketsPanel';

const DAY_LABELS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

// 7-bit checkbox bar — matches the customer's "L M M J V S D" notation
// verbatim. Stored as Boolean[7] with position 0 = Monday.
function WeekdayPicker({ value = [false, false, false, false, false, false, false], onChange }) {
  const toggle = (i) => {
    const next = value.slice();
    next[i] = !next[i];
    onChange?.(next);
  };
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {DAY_LABELS.map((label, i) => (
        <button
          key={i}
          type="button"
          onClick={() => toggle(i)}
          style={{
            width: 36,
            height: 36,
            borderRadius: 6,
            border: value[i] ? '1px solid var(--accent-strong)' : '1px solid #d9d9d9',
            background: value[i] ? 'var(--accent-strong)' : '#fff',
            color: value[i] ? '#fff' : '#666',
            fontWeight: value[i] ? 600 : 400,
            cursor: 'pointer',
          }}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

// `defaultRole` tells the editor which side the parent context represents.
// PartnerSites' expand-row passes 'pickup' or 'delivery' so the picker
// for THAT side is pre-filled with `site.id` on a fresh create.
export default function ScheduleEditor({
  open,
  partnerId,
  site,
  defaultRole,
  editing,
  showPosition = false,
  onClose,
  onSaved,
}) {
  const { t: tPartners } = useTranslation('partners');
  const [form] = Form.useForm();
  const [siteOptions, setSiteOptions] = useState([]);
  const [itemTypes, setItemTypes] = useState([]);
  const [boxes, setBoxes] = useState([]);
  const pickupSiteId = Form.useWatch('pickupSiteId', form);
  const boxId = Form.useWatch('collectionPointId', form);

  // The pickup site's boxes; without a pickup site, every box (choosing one
  // then sets its site). The current box stays listed even once archived.
  const boxOptions = useMemo(
    () =>
      boxes
        .filter((b) => (!pickupSiteId || b.siteId === pickupSiteId) && (!b.isArchived || b.id === boxId))
        .map((b) => ({ value: b.id, label: boxLabel(b) })),
    [boxes, pickupSiteId, boxId],
  );

  // Load the partner's full Site catalog for the two pickers. Cheap —
  // a typical partner has tens of Sites; the dropdown's `showSearch`
  // filters in-memory.
  useEffect(() => {
    if (!open || !partnerId) return;
    listSitesForPartner(partnerId).then(({ response }) => {
      if (!Array.isArray(response)) return;
      setSiteOptions(
        response.filter((s) => !s.isArchived).map((s) => ({ value: s.id, label: `${s.name} — ${s.address}` })),
      );
    });
    // OVO-147 — partner item types for the default-products rows.
    getPartnerItemTypes(partnerId).then(({ response }) => {
      const list = response?.partnerItemTypes || response?.items || (Array.isArray(response) ? response : []);
      setItemTypes(list);
    });
    listCollectionPointsForPartner(partnerId).then(({ response }) => {
      setBoxes(response?.items || (Array.isArray(response) ? response : []));
    });
  }, [open, partnerId]);

  // A box belongs to one site: a different pickup site drops it.
  const onPickupChange = (siteId) => {
    const box = boxes.find((b) => b.id === form.getFieldValue('collectionPointId'));
    if (box && box.siteId !== siteId) form.setFieldsValue({ collectionPointId: null });
  };
  const onBoxChange = (id) => {
    const box = boxes.find((b) => b.id === id);
    if (box && !form.getFieldValue('pickupSiteId')) form.setFieldsValue({ pickupSiteId: box.siteId });
  };

  useEffect(() => {
    if (!open) return;
    if (editing) {
      form.setFieldsValue({
        pickupSiteId: editing.pickupSiteId ?? null,
        deliverySiteId: editing.deliverySiteId ?? null,
        collectionPointId: editing.collectionPointId ?? null,
        position: editing.position ?? null,
        weekdays: editing.weekdays,
        startDate: editing.startDate ? dayjs(editing.startDate) : null,
        endDate: editing.endDate ? dayjs(editing.endDate) : null,
        deliveryTimeStart: editing.deliveryTimeStart,
        deliveryTimeEnd: editing.deliveryTimeEnd,
        deliveryDuration: editing.deliveryDuration,
        defaultLoad: editing.defaultLoad,
        defaultWeight: editing.defaultWeight,
        defaultVolume: editing.defaultVolume,
        preparationMode: editing.preparationMode,
        defaultTypes: Array.isArray(editing.defaultTypes) ? editing.defaultTypes : [],
        defaultProducts: Array.isArray(editing.defaultProducts) ? editing.defaultProducts : [],
        isActive: editing.isActive !== false,
        pausedFrom: editing.pausedFrom ? dayjs(editing.pausedFrom) : null,
        pausedUntil: editing.pausedUntil ? dayjs(editing.pausedUntil) : null,
        notes: editing.notes,
      });
    } else {
      form.resetFields();
      // Pre-fill the side that matches the row the operator opened
      // "Add schedule" from. Defaults to delivery if the caller didn't
      // tell us which side.
      const side = defaultRole === 'pickup' ? 'pickup' : 'delivery';
      form.setFieldsValue({
        pickupSiteId: side === 'pickup' ? site?.id ?? null : null,
        deliverySiteId: side === 'delivery' ? site?.id ?? null : null,
        weekdays: [false, false, false, false, false, false, false],
        startDate: dayjs(),
        isActive: true,
      });
    }
  }, [open, editing, form, site?.id, defaultRole]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (!values.pickupSiteId && !values.deliverySiteId) {
        Modal.error({
          title: 'Need a pickup or delivery Site',
          content: 'Pick at least one Site (pickup or delivery) for this recurring schedule.',
        });
        return;
      }
      const dto = buildScheduleDto(values, { editing: !!editing });
      const { error } = editing ? await updateSchedule(editing.id, dto) : await createSchedule(partnerId, dto);
      if (error) throw new Error(error.message || 'Save failed');
      onSaved?.();
    } catch (e) {
      if (e?.errorFields) return; // AntD validation surfaced inline
      Modal.error({ title: 'Failed to save schedule', content: e?.message || String(e) });
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={editing ? 'Edit schedule' : site?.name ? `Add schedule — ${site.name}` : 'Add recurring schedule'}
      onOk={handleSave}
      okText={editing ? 'Save' : 'Create'}
      width={560}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        {/* OVO-113 — two Site pickers, either or both. The materialization
            job uses each Site's name/address for its side; the OVO-120
            tour resolver fills the empty side if the partner has tour
            mapping configured. */}
        {/* minWidth: 0 — flex children default to min-width:auto, so a
            Select holding a long single-line Site label ("EPI Parking SPARK
            — Chemin du Champ-des-Filles 6A…") sets the item's MINIMUM width
            and pushes the row past the modal's edge. Allowing shrink makes
            the Select ellipsize instead. */}
        <div style={{ display: 'flex', gap: 12 }}>
          <Form.Item name="pickupSiteId" label="Pickup site" style={{ flex: 1, minWidth: 0 }}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="(none — let tour mapping fill it)"
              options={siteOptions}
              onChange={onPickupChange}
            />
          </Form.Item>
          <Form.Item name="deliverySiteId" label="Delivery site" style={{ flex: 1, minWidth: 0 }}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="(none — let tour mapping fill it)"
              options={siteOptions}
            />
          </Form.Item>
        </div>
        <div style={{ marginBottom: 12, color: 'var(--text-muted)', fontSize: 12 }}>
          <Tag color="blue" style={{ marginInlineEnd: 8 }}>
            tip
          </Tag>
          Pick at least one side. If you leave the other blank, the partner's tour mapping (if configured) will fill it
          at order-create time.
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <Form.Item
            name="collectionPointId"
            label={tPartners('recurring.box')}
            tooltip={tPartners('recurring.boxTooltip')}
            extra={pickupSiteId && boxOptions.length === 0 ? tPartners('recurring.noBoxAtSite') : undefined}
            style={{ flex: 2, minWidth: 0 }}
          >
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder={tPartners('recurring.boxPlaceholder')}
              options={boxOptions}
              disabled={!!pickupSiteId && boxOptions.length === 0}
              onChange={onBoxChange}
            />
          </Form.Item>
          {(showPosition || editing?.position != null) && (
            <Form.Item
              name="position"
              label={tPartners('recurring.position')}
              tooltip={tPartners('recurring.positionTooltip')}
              style={{ flex: 1 }}
            >
              <InputNumber style={{ width: '100%' }} min={1} precision={0} placeholder="1, 2, 3…" />
            </Form.Item>
          )}
        </div>

        <Form.Item
          name="weekdays"
          label="Recurrence (L M M J V S D)"
          rules={[
            {
              validator: (_, v) =>
                Array.isArray(v) && v.some(Boolean)
                  ? Promise.resolve()
                  : Promise.reject(new Error('Pick at least one day')),
            },
          ]}
        >
          <WeekdayPicker />
        </Form.Item>

        <div style={{ display: 'flex', gap: 12 }}>
          <Form.Item
            name="startDate"
            label="Start"
            style={{ flex: 1 }}
            rules={[{ required: true, message: 'Required' }]}
          >
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="endDate" label="End (optional)" style={{ flex: 1 }}>
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" allowClear />
          </Form.Item>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <Form.Item name="deliveryTimeStart" label="Delivery window start" style={{ flex: 1 }}>
            <Input placeholder="09:00" />
          </Form.Item>
          <Form.Item name="deliveryTimeEnd" label="Delivery window end" style={{ flex: 1 }}>
            <Input placeholder="12:00" />
          </Form.Item>
          <Form.Item
            name="deliveryDuration"
            label={tPartners('serviceTimes.scheduleDuration')}
            tooltip={tPartners('serviceTimes.scheduleDurationTooltip')}
            style={{ flex: 1 }}
          >
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <Form.Item name="defaultLoad" label="Default load" style={{ flex: 1 }}>
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="defaultWeight" label="Default weight" style={{ flex: 1 }}>
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="defaultVolume" label="Default volume" style={{ flex: 1 }}>
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
        </div>

        {/* OVO-223 — order types stamped onto every materialized order's
            `types` (skills / tour matching, e.g. "ET-Mercredi-Van-GVA-A-U").
            Free-text tags: type a value + Enter (or comma) to add. */}
        <Form.Item name="defaultTypes" label="Types (copied onto each order)" style={{ marginBottom: 12 }}>
          <Select
            mode="tags"
            tokenSeparators={[',']}
            placeholder="e.g. ET-Mercredi-Van-GVA-A-U"
            style={{ width: '100%' }}
          />
        </Form.Item>

        {/* OVO-147 — default products copied onto every materialized order
            as OrderProducts. For EPI-style collections this is where the
            operator says "this recurring visit collects OM / compost".
            Weight left blank on a requires-weight item type = the driver
            captures it at pickup (pickup-weight enrichment). */}
        <Form.Item label="Products (copied onto each order)" style={{ marginBottom: 4 }}>
          <Form.List name="defaultProducts">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div key={key} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
                    <Form.Item
                      {...restField}
                      name={[name, 'partnerItemTypeId']}
                      rules={[{ required: true, message: 'Pick a type' }]}
                      style={{ flex: 2, marginBottom: 0 }}
                    >
                      <Select
                        showSearch
                        optionFilterProp="label"
                        placeholder="Item type"
                        options={itemTypes.map((t) => ({
                          value: t.id,
                          label: `${t.name}${t.unitOfMeasure ? ` (${t.unitOfMeasure})` : ''}`,
                        }))}
                      />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'quantity']}
                      initialValue={1}
                      rules={[{ required: true, message: 'Qty' }]}
                      style={{ width: 90, marginBottom: 0 }}
                    >
                      <InputNumber style={{ width: '100%' }} min={1} placeholder="Qty" />
                    </Form.Item>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prev, cur) =>
                        prev.defaultProducts?.[name]?.partnerItemTypeId !==
                        cur.defaultProducts?.[name]?.partnerItemTypeId
                      }
                    >
                      {() => {
                        const tId = form.getFieldValue(['defaultProducts', name, 'partnerItemTypeId']);
                        const t = itemTypes.find((x) => x.id === tId);
                        return (
                          <Form.Item
                            {...restField}
                            name={[name, 'weight']}
                            style={{ width: 140, marginBottom: 0 }}
                            tooltip={
                              t?.requiresWeight
                                ? 'Leave empty to require the driver to enter the weight at pickup'
                                : undefined
                            }
                          >
                            <InputNumber
                              style={{ width: '100%' }}
                              min={0}
                              placeholder={t?.requiresWeight ? 'driver captures' : 'Weight'}
                              parser={(v) =>
                                v == null
                                  ? ''
                                  : String(v)
                                      .replace(/,/g, '.')
                                      .replace(/[^\d.-]/g, '')
                              }
                              formatter={(v) => (v == null || v === '' ? '' : String(v))}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                    <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(name)} />
                  </div>
                ))}
                <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add({ quantity: 1 })}>
                  Add product
                </Button>
                {itemTypes.length === 0 && (
                  <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 4 }}>
                    This partner has no item types yet — create them on the partner&apos;s Overview tab first.
                  </div>
                )}
              </>
            )}
          </Form.List>
        </Form.Item>

        <Form.Item name="preparationMode" label="Preparation mode">
          <Input placeholder="Normal" />
        </Form.Item>

        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="pausedFrom" label="Paused from" style={{ flex: 1 }}>
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" allowClear />
          </Form.Item>
          <Form.Item name="pausedUntil" label="Paused until" style={{ flex: 1 }}>
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" allowClear />
          </Form.Item>
        </div>

        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
      {/* #132 — tickets about this recurring schedule */}
      {editing?.id && (
        <LinkedTicketsPanel
          filter={{ orderScheduleId: editing.id }}
          prefill={{ orderScheduleId: editing.id, partnerId }}
          style={{ marginTop: 12 }}
        />
      )}
    </Modal>
  );
}
